/**
 * Cache d'enrichissement musical — inspection et purge.
 *
 * Le cache traduit une **référence d'enregistrement** (l'`acrid`, l'identifiant
 * que le référentiel donne au titre reconnu) en fiche : titre, artistes, œuvre,
 * ISWC, compositeurs. Chaque entrée absente coûte un appel facturé au
 * fournisseur, ce qui explique deux choix visibles ici :
 *
 * - le filtre de statut est OBLIGATOIRE — l'index a deux partitions et une
 *   liste « tout confondu » rendrait une pagination fausse ;
 * - le curseur est une chaîne OPAQUE. Ne jamais l'inspecter ni le reconstruire :
 *   c'est un `ExclusiveStartKey` DynamoDB encodé, précisément pour traverser la
 *   camélisation de `json_response` sans être abîmé (même piège que
 *   `payloadCase.ts`).
 *
 * L'ISRC est ici une donnée d'AFFICHAGE, pas une clé — il est facultatif, et
 * c'est précisément parce qu'il l'est que la clé a cessé d'être lui.
 */

import { apiCall } from "@/api/client";

/** `hit` = le fournisseur connaissait ; `miss` = il ne connaissait pas. */
export type MetadataStatus = "hit" | "miss";

/**
 * Qui a répondu — une clé du registre de fournisseurs du backend.
 *
 * Volontairement ouvert, et non une union fermée : le catalogue vit côté
 * backend, et une union figée ici obligerait à toucher au frontend pour
 * chaque fournisseur ajouté — après quoi elle finirait quand même périmée.
 * Les libellés se lisent dans ce même catalogue (`listIntegrations`).
 */
export type MetadataSource = string;

export interface MetadataEntry {
  acrid: string;
  status: MetadataStatus;
  /**
   * Les fournisseurs qui ont répondu. Vide dans la LISTE : l'index ne projette
   * pas la carte des fiches, et la rapatrier coûterait 10 Ko par ligne pour
   * afficher un mot. C'est le détail qui le dit.
   */
  sources: MetadataSource[];
  /**
   * Absents sur un `miss` : il n'y a pas de fiche à dénormaliser. L'`isrc` peut
   * manquer même sur un `hit` — tous les enregistrements n'en portent pas.
   */
  isrc: string | null;
  title: string | null;
  artist: string | null;
  /** Epoch secondes. */
  fetchedAt: number | null;
  /**
   * Epoch secondes — seulement sur un `miss` : un titre reconnu n'expire plus
   * (décision du 2026-08-03), son rafraîchissement passe par « Oublier ».
   */
  expiresAt: number | null;
}

export interface MetadataPage {
  items: MetadataEntry[];
  nextCursor: string | null;
}

/** Ce qu'un fournisseur a laissé sur l'enregistrement. */
export interface MetadataProviderEntry {
  /** La fiche telle que le fournisseur l'a rendue — jamais camélisée. */
  record: Record<string, unknown>;
  /** `isrc` | `search` | `acrid` — comment l'appariement s'est fait. */
  matched_by: string;
  fetched_at: number;
}

/** Un « inconnu » mémorisé, avec son échéance. */
export interface MetadataMissEntry {
  at: number;
  expires_at: number;
}

/**
 * Le détail porte en plus les réponses des fournisseurs, telles quelles.
 *
 * ATTENTION : il n'étend PAS `MetadataEntry`, et ce n'est pas un oubli : cette route est
 * la seule à traverser `convert_keys=False` côté backend. C'est indispensable —
 * caméliser `payload` ferait diverger ce que l'opérateur lit de ce que le
 * dataplane reçoit — mais ça s'applique à **toute** la réponse, enveloppe
 * comprise. Les dates ressortent donc en `fetched_at` / `expires_at`, là où la
 * liste rend `fetchedAt` / `expiresAt`.
 *
 * Hériter de `MetadataEntry` promettait donc un `fetchedAt` qui vaut
 * `undefined` à l'exécution — avec TypeScript pour garantir le contraire.
 * Vérifié sur la Lambda déployée le 2026-07-28.
 */
export interface MetadataEntryDetail {
  /** Identiques dans les deux formes : aucun underscore à caméliser. */
  acrid: string;
  status: MetadataStatus;
  sources: MetadataSource[];
  isrc: string | null;
  title: string | null;
  artist: string | null;
  /** Epoch secondes — en snake_case ICI, voir ci-dessus. */
  fetched_at: number | null;
  expires_at: number | null;
  /**
   * Une entrée par fournisseur qui a répondu.
   *
   * ``Record`` et non ``Partial<Record<…>>`` : la clé est ouverte, donc
   * « toutes les clés ne sont pas là » va de soi. Le dire avec ``Partial``
   * rendrait en plus chaque valeur ÉNUMÉRÉE possiblement absente, ce qui est
   * faux — une clé présente porte toujours son entrée.
   */
  providers: Record<MetadataSource, MetadataProviderEntry>;
  /**
   * Une entrée par fournisseur qui a dit « inconnu ». C'est ce qui explique
   * qu'un titre ne soit pas re-cherché — donc la première chose à regarder
   * quand on se demande pourquoi.
   */
  misses: Record<MetadataSource, MetadataMissEntry>;
}

export interface MetadataStats {
  approximateEntries: number;
  sizeBytes: number;
  refreshedByAwsEveryHours: number;
}

export function listMetadataEntries(
  token: string,
  status: MetadataStatus,
  cursor?: string | null,
  limit = 50,
): Promise<MetadataPage> {
  const params = new URLSearchParams({ status, limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  return apiCall<MetadataPage>(`/metadata/entries?${params}`, { token });
}

export function getMetadataEntry(
  token: string,
  acrid: string,
): Promise<MetadataEntryDetail> {
  // Le détail traverse `convert_keys=False` côté backend : les clés de
  // `payload` sont celles du fournisseur, en snake_case. Ne pas les convertir.
  return apiCall<MetadataEntryDetail>(
    `/metadata/entries/${encodeURIComponent(acrid)}`,
    { token },
  );
}

/**
 * Oublie une entrée pour que la prochaine résolution la reconstruise.
 *
 * ATTENTION : cette reconstruction est un appel FACTURÉ au fournisseur. C'est une
 * action par entrée, jamais en masse — et l'écran le dit avant de la lancer.
 */
export function purgeMetadataEntry(
  token: string,
  acrid: string,
): Promise<{ acrid: string; purged: boolean }> {
  return apiCall(`/metadata/entries/${encodeURIComponent(acrid)}`, {
    token,
    method: "DELETE",
  });
}

export function getMetadataStats(token: string): Promise<MetadataStats> {
  return apiCall<MetadataStats>("/metadata/stats", { token });
}
